import { Form, FormCheck, ModalBody, ModalFooter, Spinner } from "react-bootstrap";
import { Formik, getIn } from "formik";
import withModal from "../withModal";
import Btn from "../../ui/button/Btn";
import Input from "../../ui/form/Input";
import CustomSelect from "../../ui/form/CustomSelect";
import { customerSchema } from "../../schema/validationSchema";
import { handleApiError } from "../../utility/handleApiError";
import { useToast } from "../../helpers/contexts/ToastContext";
import { useDispatch, useSelector } from "react-redux";
import { addCustomer, updateCustomer, fetchCustomerCombinedSettings } from "../../redux/slice/customerSlice";
import { fetchAll } from "../../redux/slice/configurationSlice";
import { fetchAllCities, fetchUsersList } from "../../redux/slice/usersSlice";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { FormLabel } from "../../ui/form/utils";
import { MinusIcon } from "../../icons/btnIcons";
import CustomTabs from "../../ui/CustomTabs";
import QuickCityModal from "./QuickCityModal";

const Additional_Tabs = [
  { key: "address", title: "Address" },
  { key: "persons", title: "Persons" },
  { key: "other_details", title: "Other Details" },
];

// HELPERS
const SLUG_MAP = {
  industries: "industries",
  sources: "sources",
  companyTypes: "company-types",
};

const normalizeOptions = (list = []) =>
  list.map((i) => ({
    label: String(i.city_label || i.name || ""),
    value: i.id,
  }));

const normalizeUser = (list = []) =>
  list.map((i) => ({
    label: String(i.name || ""),
    value: i.id,
  }));

const normalizeUrl = (url) => {
  if (!url || url === "null" || url === undefined || url === "—" || url === "-") return "";

  const urlStr = String(url).trim();

  if (!urlStr || urlStr === "null" || urlStr === "—" || urlStr === "-") return "";

  return /^(https?:\/\/)/i.test(urlStr) ? urlStr : `https://${urlStr}`;
};

const AddressSynchronizer = ({ values, setFieldValue }) => {
  const prevCopyValue = useRef(values.copy_billing_address);

  useEffect(() => {
    if (values.copy_billing_address) {
      setFieldValue("shipping_street", values.billing_street || "");
      setFieldValue("shipping_city", values.billing_city);
      setFieldValue("shipping_zip", values.billing_zip || "");
    } else if (prevCopyValue.current && !values.copy_billing_address) {
      setFieldValue("shipping_street", "");
      setFieldValue("shipping_city", null);
      setFieldValue("shipping_zip", "");
    }
    prevCopyValue.current = values.copy_billing_address;
  }, [
    values.copy_billing_address,
    values.billing_street,
    values.billing_city,
    values.billing_zip,
    setFieldValue,
  ]);

  return null;
};

const CustomerModalInner = ({ isEdit, onClose, data, onSave, isLoading }) => {
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const user  = useSelector((state) => state.users?.userList);
  const debounceRef = useRef();
  const dropdownsLoadedRef = useRef(false);
  const formikRef = useRef(null);

  const [dropdownOptions, setDropdownOptions] = useState({
    industries: [],
    sources: [],
    companyTypes: [],
    natures: [],
    users: [],
    billingCities: [],
    shippingCities: [],
  });

  const [loading, setLoading] = useState({
    industries: false,
    sources: false,
    companyTypes: false,
    users: false,
    billingCities: false,
    shippingCities: false,
  });

  const [activeTab, setActiveTab] = useState("address");
  const [pendingCityName, setPendingCityName] = useState("");
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [cityModalTarget, setCityModalTarget] = useState("billingCities"); // "billingCities" or "shippingCities"

  // Load initial dropdown data
  useEffect(() => {
    if (dropdownsLoadedRef.current) return;

    const loadDropdowns = async () => {
      dropdownsLoadedRef.current = true;
      try {
        setLoading((prev) => ({
          ...prev,
          industries: true,
          sources: true,
          companyTypes: true,
          users: true,
        }));

        const basePayload = { page: 1, limit: 10, is_active: true };

        const [combinedSettings, users, cities] = await Promise.all([
          dispatch(fetchCustomerCombinedSettings()).unwrap(),
          dispatch(fetchUsersList(basePayload)).unwrap(),
          dispatch(fetchAllCities(basePayload)).unwrap(),
        ]);

        const normalizedCities = normalizeOptions(cities?.Cities || cities?.data || []);

        setDropdownOptions((prev) => ({
          ...prev,
          industries: normalizeOptions(combinedSettings?.industries?.data || []),
          sources: normalizeOptions(combinedSettings?.sources?.data || []),
          companyTypes: normalizeOptions(combinedSettings?.company_types?.data || []),
          natures: (combinedSettings?.customer_natures?.data || [])
            .filter((n) => n.is_active !== false)
            .map((n) => ({ label: n.name, value: n.id, color: n.color_code, isDefault: n.is_default })),
          users: normalizeUser(users?.data || []),
          billingCities: normalizedCities,
          shippingCities: normalizedCities,
        }));
      } catch (error) {
        console.error("Error loading dropdowns:", error);
        showToast("Failed to load dropdown options", "error");
      } finally {
        setLoading((prev) => ({
          ...prev,
          industries: false,
          sources: false,
          companyTypes: false,
          users: false,
        }));
      }
    };

    loadDropdowns();
  }, [dispatch, showToast]);

  useEffect(() => {
    if (!isEdit || !data) return;

    setDropdownOptions((prev) => {
      const next = { ...prev };

      // Inject industry option if present
      if (data.industry?.id && data.industry?.name) {
        const exists = prev.industries.some((o) => o.value === data.industry.id);
        if (!exists) {
          next.industries = [
            { label: data.industry.name, value: data.industry.id },
            ...prev.industries,
          ];
        }
      }

      // Inject source option if present
      if (data.source?.id && data.source?.name) {
        const exists = prev.sources.some((o) => o.value === data.source.id);
        if (!exists) {
          next.sources = [{ label: data.source.name, value: data.source.id }, ...prev.sources];
        }
      }

      // Inject company type option if present
      if (data.customer_type?.id && data.customer_type?.name) {
        const exists = prev.companyTypes.some((o) => o.value === data.customer_type.id);
        if (!exists) {
          next.companyTypes = [
            { label: data.customer_type.name, value: data.customer_type.id },
            ...prev.companyTypes,
          ];
        }
      }

      // Inject assigned_to user option if present
      if (data.assigned_to?.id && data.assigned_to?.name) {
        const exists = prev.users.some((o) => o.value === data.assigned_to.id);
        if (!exists) {
          next.users = [
            { label: data.assigned_to.name, value: data.assigned_to.id },
            ...prev.users,
          ];
        }
      }

      return next;
    });
  }, [isEdit, data]);

  const handleDropdownSearch = useCallback(
    (type, inputValue, meta) => {
      if (['industries', 'sources', 'companyTypes'].includes(type)) {
        return;
      }
      
      if (!meta || (meta.action !== "input-change" && meta.action !== "menu-open" && meta.action !== "set-value")) {
        return;
      }

      const searchText = String(inputValue || "");
      if (debounceRef.current) clearTimeout(debounceRef.current);

      setLoading((prev) => ({ ...prev, [type]: true }));

      if (meta && (meta.action === "menu-open" || meta.action === "input-change" || meta.action === "set-value")) {
        setDropdownOptions((prev) => ({ ...prev, [type]: [] }));
      }

      debounceRef.current = setTimeout(async () => {
        try {
          const params = {
            page: 1,
            limit: 10,
            is_active: true,
            ...(searchText && { search: searchText }),
          };

          let result;

          if (type === "users") {
            result = await dispatch(fetchUsersList(params)).unwrap();
          } else if (type === "billingCities" || type === "shippingCities") {
            result = await dispatch(fetchAllCities(params)).unwrap();
          } else {
            result = await dispatch(fetchAll({ slug: SLUG_MAP[type], params })).unwrap();
          }

          setDropdownOptions((prev) => ({
            ...prev,
            [type]:
              type === "billingCities" || type === "shippingCities"
                ? normalizeOptions(result?.Cities || result?.data || [])
                : type === "users"
                  ? normalizeUser(result?.data || [])
                  : normalizeOptions(result?.data || []),
          }));
        } catch (error) {
          console.error(`Error searching ${type}:`, error);
        } finally {
          setLoading((prev) => ({ ...prev, [type]: false }));
        }
      }, 300);
    },
    [dispatch]
  );

  // Nature a new customer gets unless another is picked (Cold by default)
  const defaultNature = useMemo(
    () => dropdownOptions.natures.find((n) => n.isDefault) || null,
    [dropdownOptions.natures],
  );

  const getOption = useCallback(
    (type, id) => {
      if (!id) return null;
      return dropdownOptions[type]?.find((opt) => String(opt.value) === String(id)) || null;
    },
    [dropdownOptions]
  );

  const initialValues = useMemo(() => {
    if (isEdit && data) {
      const addressesArray = data.addresses || [];
      const billingAddr =
        addressesArray.find((a) => a.address_type === "billing") || addressesArray[0];
      const shippingAddr = addressesArray.find((a) => a.address_type === "shipping");

      const billingStreet = billingAddr?.address_line_1 || "";
      const billingZip = billingAddr?.pincode || "";
      const billingCityLabel = billingAddr?.city_label || billingAddr?.city || "";

      const shippingStreet = shippingAddr?.address_line_1 || "";
      const shippingZip = shippingAddr?.pincode || "";
      const shippingCityLabel = shippingAddr?.city_label || shippingAddr?.city || "";

      const billingCityOption = billingCityLabel
        ? { label: billingCityLabel, value: billingCityLabel }
        : null;

      const assignedToOption = data.assigned_to?.id
        ? { label: data.assigned_to.name, value: data.assigned_to.id }
        : null;

      const industryOption = data.industry?.id
        ? { label: data.industry.name, value: data.industry.id }
        : data.industry_id
          ? getOption("industries", data.industry_id)
          : null;

      const sourceOption = data.source?.id
        ? { label: data.source.name, value: data.source.id }
        : data.source_id
          ? getOption("sources", data.source_id)
          : null;

      const companyTypeOption = data.customer_type?.id
        ? { label: data.customer_type.name, value: data.customer_type.id }
        : data.company_type_id
          ? getOption("companyTypes", data.company_type_id)
          : null;

      const natureOption = data.nature?.id
        ? { label: data.nature.name, value: data.nature.id, color: data.nature.color_code }
        : null;

      const websiteValue = normalizeUrl(data?.website ?? "");

      const persons = (data.persons || []).map((p) => ({
        id: p.id,
        name: String(p.name || ""),
        email: String(Array.isArray(p.emails) && p.emails.length > 0 ? p.emails[0] : p.email || ""),
        phone: String(
          Array.isArray(p.phones) && p.phones.length > 0 ? p.phones[0] : p.phone || p.mobile || ""
        ),
        designation: String(p.designation || ""),
      }));

      const initialPersons =
        persons.length > 0
          ? persons.map((p) => ({
              name: String(p.person_name || p.name || ""),
              email: String(p.email || ""),
              phone: String(p.phone || p.mobile || ""),
              designation: String(p.job_title || p.designation || ""),
            }))
          : [];

      return {
        contact_person: String(data.contact_person || ""),
        person_designation: String(data.person_designation || ""),
        mobile: String(data.mobile || ""),
        email: String(data.email || ""),
        company_name: String(data.company_name || ""),
        trade_name: String(data.trade_name || ""),
        website: websiteValue,
        company_type: companyTypeOption,
        industry: industryOption,
        source: sourceOption,
        customer_nature: natureOption,
        assigned_to: assignedToOption,
        gstnumber: String(data.gstnumber || data.gstin || ""),
        copy_billing_address:
          Boolean(data.copy_billing_address) ||
          (billingStreet === shippingStreet &&
            billingCityLabel === shippingCityLabel &&
            billingZip === shippingZip &&
            !!billingStreet),
        billing_street: String(billingStreet),
        billing_city: billingCityOption,
        billing_zip: String(billingZip),
        shipping_street: String(shippingStreet),
        shipping_city: shippingCityLabel
          ? { label: shippingCityLabel, value: shippingCityLabel }
          : null,
        shipping_zip: String(shippingZip),
        persons:
          initialPersons.length > 0
            ? initialPersons
            : [{ name: "", email: "", phone: "", designation: "" }],
        business_type: String(data.business_type || "business"),
      };
    }

    return {
      company_name: "",
      contact_person: "",
      email: "",
      mobile: "",
      person_designation: "",
      trade_name: "",
      website: "",
      company_type: null,
      industry: null,
      source: null,
      customer_nature: null,
      assigned_to: user ? { label: "Me", value: user.id } : null,
      gstnumber: "",
      copy_billing_address: false,
      billing_street: "",
      billing_city: null,
      billing_zip: "",
      shipping_street: "",
      shipping_city: null,
      shipping_zip: "",
      persons: [
        {
          name: "",
          email: "",
          phone: "",
          designation: "",
        },
      ],
      business_type: "business",
    };
  }, [isEdit, data, getOption, user]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      const payload = {
        company_name: String(values.company_name),
        website: String(values.website || ""),
        company_type_id: values.company_type?.value || null,
        industry_id: values.industry?.value || null,
        source_id: values.source?.value || null,
        // Left out when nothing is picked: the API keeps the current one / sets the default (Cold)
        customer_nature_id: values.customer_nature?.value || defaultNature?.value || undefined,
        assigned_to: values.assigned_to?.value || null,
        business_type: String(values.business_type),
        is_active: true,

        contact_person: String(values.contact_person || ""),
        email: String(values.email || ""),
        mobile: String(values.mobile || ""),
        person_designation: String(values.person_designation || ""),
        address: String(values.billing_street || ""),
        city: values.billing_city?.label || "",
        gstnumber: String(values.gstnumber || ""),

        addresses: [
          {
            address_type: "billing",
            address_line_1: values.billing_street,
            pincode: values.billing_zip,
            city: values.billing_city?.label,
          },
          {
            address_type: "shipping",
            address_line_1: values.shipping_street,
            pincode: values.shipping_zip,
            city: values.shipping_city?.label,
          },
        ],

        copy_billing_address: values.copy_billing_address ? 1 : 0,

        persons: values.persons
          .filter((p) => p.name)
          .map((p) => ({
            id: p.id || undefined,
            name: p.name,
            designation: p.designation,
            phones: p.phone ? [p.phone] : [],
            email: p.email || null,
            is_active: true,
          })),
      };

      const action =
        isEdit && data?.id ? updateCustomer({ id: data.id, payload }) : addCustomer(payload);

      await dispatch(action).unwrap();

      showToast(`Customer ${isEdit ? "updated" : "created"} successfully!`, "success");
      onSave?.();
      onClose?.();
    } catch (err) {
      handleApiError(err, { setErrors, showToast });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={customerSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          isSubmitting,
          setFieldValue,
        }) => {
          return (
            <Form onSubmit={handleSubmit} className="flex-grow-1 overflow-hidden flex-column">
              <AddressSynchronizer values={values} setFieldValue={setFieldValue} />
              <ModalBody className="p-12 overflow-y-auto my-0 custom-scroll">
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "600px" }}
                  >
                    <Spinner animation="border" variant="primary" size="md" />
                  </div>
                ) : (
                  <>
                    <div className="flex-column gap-12">
                      {/* Basic Details */}
                      <div className="grid columns-2 row-gap-8 column-gap-10">
                        <h6 className="section-title g-col-2 mb-4">Basic Details</h6>
                        <div className="form-input-cover g-col-2 d-flex gap-10 mb-4">
                          <FormLabel
                            className="text-primary m-0"
                            id="business_type"
                            labelName="Customer Type"
                          />
                          <div className="d-flex gap-4">
                            <FormCheck
                              type="radio"
                              name="business_type"
                              id="business"
                              label="Business"
                              checked={values.business_type === "business"}
                              onChange={() => setFieldValue("business_type", "business")}
                            />
                            <FormCheck
                              type="radio"
                              name="business_type"
                              id="individual"
                              label="Individual"
                              checked={values.business_type === "individual"}
                              onChange={() => setFieldValue("business_type", "individual")}
                            />
                          </div>
                        </div>

                        <Input
                          label={{ name: "Company Name" }}
                          input={{
                            type: "text",
                            name: "company_name",
                            placeholder: "Enter Company Name",
                            value: values.company_name,
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                          validation={{
                            isInvalid: touched.company_name && !!errors.company_name,
                            message: errors.company_name,
                          }}
                        />

                        <Input
                          label={{ name: "Contact Person" }}
                          input={{
                            type: "text",
                            name: "contact_person",
                            placeholder: "Enter Contact Person",
                            value: values.contact_person,
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                          validation={{
                            isInvalid: touched.contact_person && !!errors.contact_person,
                            message: errors.contact_person,
                          }}
                        />

                        <Input
                          label={{ name: "Email" }}
                          input={{
                            type: "email",
                            name: "email",
                            placeholder: "Enter Email",
                            value: values.email,
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                          validation={{
                            isInvalid: touched.email && !!errors.email,
                            message: errors.email,
                          }}
                        />

                        <Input
                          label={{ name: "Mobile" }}
                          input={{
                            type: "phone",
                            name: "mobile",
                            value: values.mobile,
                            onChange: (val) => setFieldValue("mobile", val),
                            onBlur: handleBlur,
                          }}
                          validation={{
                            isInvalid: touched.mobile && !!errors.mobile,
                            message: errors.mobile,
                          }}
                        />

                        <CustomSelect
                          label={{ name: "Assigned To" }}
                          selectProps={{
                            placeholder: "Select Assigned To",
                          }}
                          onInputChange={(inputValue, meta) => {
                            handleDropdownSearch("users", inputValue, meta);
                          }}
                          onMenuOpen={() =>
                            handleDropdownSearch("users", "", {
                              action: "menu-open",
                            })
                          }
                          options={dropdownOptions.users || []}
                          value={values.assigned_to}
                          onChange={(opt) => setFieldValue("assigned_to", opt)}
                          isSearchable={true}
                          validation={{
                            isInvalid: touched.assigned_to && !!errors.assigned_to,
                            message: errors.assigned_to,
                          }}
                        />

                        <CustomSelect
                          label={{ name: "Customer Nature" }}
                          selectProps={{
                            placeholder: "Select Nature",
                          }}
                          options={dropdownOptions.natures || []}
                          value={values.customer_nature || (isEdit ? null : defaultNature)}
                          onChange={(opt) => setFieldValue("customer_nature", opt)}
                          isSearchable={false}
                          isLoading={loading.industries}
                        />

                        <Input
                          label={{ name: "Person Designation" }}
                          input={{
                            type: "text",
                            name: "person_designation",
                            placeholder: "Enter Person Designation",
                            value: values.person_designation,
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                          validation={{
                            isInvalid: touched.person_designation && !!errors.person_designation,
                            message: errors.person_designation,
                          }}
                        />
                      </div>

                      <CustomTabs
                        items={Additional_Tabs}
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        variant="underline"
                        className="mb-0"
                      />
                    </div>

                    <div className="grid columns-2 row-gap-8 column-gap-10 mt-12">
                      {/* Address Tab */}
                      {activeTab === "address" && (
                        <>
                          <div className="text-end g-col-2">
                            <FormCheck
                              label="Copy As Billing"
                              type="checkbox"
                              id="copy-billing-address"
                              name="copy_billing_address"
                              checked={values.copy_billing_address}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Billing Address */}
                          <div className="flex-column gap-8">
                            <Input
                              label={{ name: "Billing Address" }}
                              input={{
                                type: "text",
                                name: "billing_street",
                                placeholder: "Enter Billing Address",
                                value: values.billing_street,
                                onChange: handleChange,
                                onBlur: handleBlur,
                              }}
                              validation={{
                                isInvalid: touched.billing_street && !!errors.billing_street,
                                message: errors.billing_street,
                              }}
                            />
                            <CustomSelect
                              label={{ name: "City" }}
                              selectProps={{
                                placeholder: "City",
                              }}
                              noOptionsAction={{
                                label: pendingCityName
                                  ? `Create "${pendingCityName}"`
                                  : "Create City",
                                onClick: () => {
                                  setCityModalTarget("billingCities");
                                  setCityModalOpen(true);
                                },
                              }}
                              onInputChange={(val, meta) => {
                                if (meta?.action === "input-change") {
                                  setPendingCityName(String(val || "").trim());
                                }
                                handleDropdownSearch("billingCities", String(val || ""), meta);
                              }}
                              onMenuOpen={() => {
                                handleDropdownSearch("billingCities", "", {
                                  action: "menu-open",
                                });
                              }}
                              options={dropdownOptions.billingCities}
                              value={values.billing_city}
                              lookLikeInput={true}
                              isClearable={true}
                              isSearchable={true}
                              isLoading={loading.billingCities}
                              onChange={(opt) => setFieldValue("billing_city", opt)}
                              validation={{
                                isInvalid: touched.billing_city && !!errors.billing_city,
                                message: errors.billing_city,
                              }}
                              menuPosition="fixed"
                              menuPlacement="auto"
                            />
                            <Input
                              label={{ name: "Pin Code" }}
                              input={{
                                type: "text",
                                name: "billing_zip",
                                placeholder: "Pin Code",
                                value: values.billing_zip,
                                onChange: (e) => {
                                  e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
                                  handleChange(e);
                                },
                                onBlur: handleBlur,
                              }}
                              validation={{
                                isInvalid: touched.billing_zip && !!errors.billing_zip,
                                message: errors.billing_zip,
                              }}
                            />
                          </div>

                          {/* Shipping Address */}
                          <div className="flex-column gap-8">
                            <Input
                              label={{ name: "Shipping Address" }}
                              input={{
                                type: "text",
                                name: "shipping_street",
                                placeholder: "Enter Shipping Address",
                                value: values.shipping_street,
                                onChange: handleChange,
                                onBlur: handleBlur,
                                disabled: values.copy_billing_address,
                              }}
                              validation={{
                                isInvalid: touched.shipping_street && !!errors.shipping_street,
                                message: errors.shipping_street,
                              }}
                            />

                            <CustomSelect
                              label={{ name: "City" }}
                              isDisabled={values.copy_billing_address}
                              selectProps={{
                                placeholder: "City",
                                isDisabled: values.copy_billing_address,
                              }}
                              noOptionsAction={{
                                label: pendingCityName
                                  ? `Create "${pendingCityName}"`
                                  : "Create City",
                                onClick: () => {
                                  setCityModalTarget("shippingCities");
                                  setCityModalOpen(true);
                                },
                              }}
                              onInputChange={(val, meta) => {
                                if (meta?.action === "input-change") {
                                  setPendingCityName(String(val || "").trim());
                                }
                                handleDropdownSearch("shippingCities", String(val || ""), meta);
                              }}
                              onMenuOpen={() => {
                                handleDropdownSearch("shippingCities", "", {
                                  action: "menu-open",
                                });
                              }}
                              options={dropdownOptions.shippingCities}
                              value={values.shipping_city}
                              lookLikeInput={true}
                              isClearable={true}
                              isSearchable={true}
                              isLoading={loading.shippingCities}
                              onChange={(opt) => setFieldValue("shipping_city", opt)}
                              validation={{
                                isInvalid: touched.shipping_city && !!errors.shipping_city,
                                message: errors.shipping_city,
                              }}
                              menuPosition="fixed"
                              menuPlacement="auto"
                            />
                            <Input
                              label={{ name: "Pin Code" }}
                              input={{
                                type: "text",
                                name: "shipping_zip",
                                placeholder: "Pin Code",
                                value: values.shipping_zip,
                                onChange: (e) => {
                                  e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
                                  handleChange(e);
                                },
                                onBlur: handleBlur,
                                disabled: values.copy_billing_address,
                              }}
                              validation={{
                                isInvalid: touched.shipping_zip && !!errors.shipping_zip,
                                message: errors.shipping_zip,
                              }}
                            />
                          </div>
                        </>
                      )}

                      {/* Persons Tab */}
                      {activeTab === "persons" && (
                        <>
                          {values.persons.map((person, index) => {
                            const nameError = getIn(errors, `persons.${index}.name`);
                            const nameTouch = getIn(touched, `persons.${index}.name`);
                            const emailError = getIn(errors, `persons.${index}.email`);
                            const emailTouch = getIn(touched, `persons.${index}.email`);
                            const phoneError = getIn(errors, `persons.${index}.phone`);
                            const phoneTouch = getIn(touched, `persons.${index}.phone`);
                            const desigError = getIn(errors, `persons.${index}.designation`);
                            const desigTouch = getIn(touched, `persons.${index}.designation`);

                            return (
                              <React.Fragment key={index}>
                                <Input
                                  label={{ name: "Name" }}
                                  input={{
                                    type: "text",
                                    name: `persons.${index}.name`,
                                    placeholder: "Enter Name",
                                    value: person.name,
                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                  }}
                                  validation={{
                                    isInvalid: nameTouch && !!nameError,
                                    message: nameError,
                                  }}
                                />
                                <Input
                                  label={{ name: "Email" }}
                                  input={{
                                    type: "email",
                                    name: `persons.${index}.email`,
                                    placeholder: "Enter Email",
                                    value: person.email,
                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                  }}
                                  validation={{
                                    isInvalid: emailTouch && !!emailError,
                                    message: emailError,
                                  }}
                                />
                                <Input
                                  label={{ name: "Phone" }}
                                  input={{
                                    type: "phone",
                                    name: `persons.${index}.phone`,
                                    value: person.phone,
                                    onChange: (val) => setFieldValue(`persons.${index}.phone`, val),
                                    onBlur: handleBlur,
                                  }}
                                  validation={{
                                    isInvalid: phoneTouch && !!phoneError,
                                    message: phoneError,
                                  }}
                                />
                                <Input
                                  label={{ name: "Designation" }}
                                  input={{
                                    type: "text",
                                    name: `persons.${index}.designation`,
                                    placeholder: "Designation",
                                    value: person.designation,
                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                  }}
                                  validation={{
                                    isInvalid: desigTouch && !!desigError,
                                    message: desigError,
                                  }}
                                />
                                <div className="d-flex justify-content-end gap-4 g-col-2">
                                  {values.persons.length - 1 === index && (
                                    <Btn
                                      variant="link"
                                      className="text-success p-0 text-decoration-none"
                                      onClick={() => {
                                        setFieldValue("persons", [
                                          ...values.persons,
                                          {
                                            name: "",
                                            email: "",
                                            phone: "",
                                            designation: "",
                                          },
                                        ]);
                                      }}
                                      title="Add Person"
                                      isAddBtn
                                    />
                                  )}
                                  {values.persons.length > 1 && (
                                    <Btn
                                      variant="link"
                                      className="text-danger p-0 text-decoration-none"
                                      onClick={() => {
                                        const newPersons = [...values.persons];
                                        newPersons.splice(index, 1);
                                        setFieldValue("persons", newPersons);
                                      }}
                                      startIcon={<MinusIcon size={14} />}
                                      title="Remove Person"
                                    />
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}

                      {/* Other Details Tab */}
                      {activeTab === "other_details" && (
                        <>
                          <Input
                            label={{ name: "Website" }}
                            input={{
                              type: "text",
                              name: "website",
                              placeholder: "Enter Website",
                              value: values.website || "",
                              onChange: handleChange,
                              onBlur: handleBlur,
                            }}
                            validation={{
                              isInvalid: touched.website && !!errors.website,
                              message: errors.website,
                            }}
                          />

                          <CustomSelect
                            label={{ name: "Company Type" }}
                            selectProps={{
                              placeholder: "Select Company Type",
                            }}
                            onInputChange={(inputValue, meta) => {
                              handleDropdownSearch("companyTypes", inputValue, meta);
                            }}
                            onMenuOpen={() =>
                              handleDropdownSearch("companyTypes", "", {
                                action: "menu-open",
                              })
                            }
                            options={dropdownOptions.companyTypes || []}
                            value={values.company_type}
                            onChange={(opt) => setFieldValue("company_type", opt)}
                            isSearchable={true}
                            isLoading={loading.companyTypes}
                            validation={{
                              isInvalid: touched.company_type && !!errors.company_type,
                              message: errors.company_type,
                            }}
                          />

                          <CustomSelect
                            label={{ name: "Industry" }}
                            selectProps={{
                              placeholder: "Select Industry",
                            }}
                            onInputChange={(inputValue, meta) => {
                              handleDropdownSearch("industries", inputValue, meta);
                            }}
                            onMenuOpen={() =>
                              handleDropdownSearch("industries", "", {
                                action: "menu-open",
                              })
                            }
                            options={dropdownOptions.industries || []}
                            value={values.industry}
                            onChange={(opt) => setFieldValue("industry", opt)}
                            isSearchable={true}
                            isLoading={loading.industries}
                            validation={{
                              isInvalid: touched.industry && !!errors.industry,
                              message: errors.industry,
                            }}
                            menuPosition="fixed"
                          />

                          <CustomSelect
                            label={{ name: "Source" }}
                            selectProps={{
                              placeholder: "Select Source",
                            }}
                            onInputChange={(inputValue, meta) => {
                              handleDropdownSearch("sources", inputValue, meta);
                            }}
                            onMenuOpen={() =>
                              handleDropdownSearch("sources", "", {
                                action: "menu-open",
                              })
                            }
                            options={dropdownOptions.sources || []}
                            value={values.source}
                            onChange={(opt) => setFieldValue("source", opt)}
                            isSearchable={true}
                            isLoading={loading.sources}
                            validation={{
                              isInvalid: touched.source && !!errors.source,
                              message: errors.source,
                            }}
                            menuPosition="fixed"
                          />

                          <Input
                            label={{ name: "GST No" }}
                            input={{
                              type: "text",
                              name: "gstnumber",
                              placeholder: "Enter GST No",
                              value: values.gstnumber,
                              onChange: handleChange,
                              onBlur: handleBlur,
                            }}
                            validation={{
                              isInvalid: touched.gstnumber && !!errors.gstnumber,
                              message: errors.gstnumber,
                            }}
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
              </ModalBody>

              {/* Footer Buttons */}
              <ModalFooter className="shadow border-top">
                <Btn
                  variant="cancel"
                  title="Cancel"
                  className="modal-btn me-3"
                  onClick={onClose}
                  size="modal"
                  disabled={isSubmitting}
                />
                <Btn
                  type="submit"
                  variant="primary"
                  title={isEdit ? "Update" : "Create"}
                  size="modal"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                />
              </ModalFooter>
            </Form>
          );
        }}
      </Formik>

      <QuickCityModal
        key={pendingCityName || "quick-city-modal"}
        isOpen={cityModalOpen}
        initialCityName={pendingCityName}
        onClose={() => {
          setCityModalOpen(false);
          setPendingCityName("");
        }}
        onSave={(newCity) => {
          const cityLabel = newCity?.name || pendingCityName;
          const cityOption = {
            label: cityLabel,
            value: newCity?.id || cityLabel,
          };
          const fieldName = cityModalTarget === "shippingCities" ? "shipping_city" : "billing_city";
          setDropdownOptions((prev) => ({
            ...prev,
            billingCities: [cityOption, ...prev.billingCities],
            shippingCities: [cityOption, ...prev.shippingCities],
          }));
          if (formikRef.current) {
            formikRef.current.setFieldValue(fieldName, cityOption);
          }
          setCityModalOpen(false);
          setPendingCityName("");
        }}
        title="Add City"
        size="md"
      />
    </>
  );
};

const CustomerModal = withModal(CustomerModalInner);
export default CustomerModal;
